// routes/Shop.jsx

import { useEffect, useMemo, useState } from 'react';
import { WebLayout } from 'components/Layout';

import { SearchBar } from '../components/SearchBar';
import { CategoryFilter } from '../components/CategoryFilter';
import { ProductCard } from '../components/ProductCard';
import { FeaturedProducts } from '../components/Featuredproducts';
import { FAQ } from '../components/FAQ';
import { CATEGORIES, getProducts } from '../api/getProducts';

export function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const data = await getProducts();
        if (!cancelled) {
          setProducts(data);
          setLoadError('');
        }
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'Could not load products.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const inStock = useMemo(() => products.filter((p) => p.stock), [products]);

  const featured = useMemo(
    () => inStock.filter((p) => p.badge === 'hot' || p.badge === 'new').slice(0, 4),
    [inStock]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return inStock.filter((p) => {
      const matchesCategory = category === 'All' || p.cat === category;
      const matchesQuery =
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.spec.toLowerCase().includes(q) ||
        p.cat.toLowerCase().includes(q);

      return matchesCategory && matchesQuery;
    });
  }, [inStock, category, query]);

  return (
    <WebLayout>
      <section className="px-4 pb-12 pt-16 text-center sm:px-8">
        <div className="mb-3 text-xs font-extrabold uppercase tracking-[0.2em] text-accent">
          The catalog
        </div>
        <h1 className="mx-auto max-w-2xl text-[clamp(2rem,5vw,3rem)] font-black leading-tight tracking-tight">
          Find your next gadget
        </h1>
        <p className="mx-auto mt-3 max-w-md text-sm text-text-muted">
          Search the full catalog or filter by category. Tap any item to order straight on WhatsApp.
        </p>

        <div className="mt-8">
          <SearchBar value={query} onChange={setQuery} />
        </div>
      </section>

      {loadError && (
        <p className="mx-auto mb-8 max-w-md text-center text-sm font-bold text-warning">
          {loadError}
        </p>
      )}

      {!loading && <FeaturedProducts products={featured} />}

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-8">
        {loading ? (
          <p className="py-16 text-center text-text-muted">Loading products…</p>
        ) : (
          <>
            <div className="mb-8 flex flex-col items-center gap-5">
              <CategoryFilter categories={CATEGORIES} active={category} onChange={setCategory} />
              <p className="text-xs text-text-dim">
                {filtered.length} {filtered.length === 1 ? 'product' : 'products'}
                {query && (
                  <>
                    {' '}
                    matching &quot;<span className="text-text-muted">{query}</span>&quot;
                  </>
                )}
              </p>
            </div>

            {filtered.length ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {filtered.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3 py-16 text-center">
                <p className="text-text-muted">
                  No products match &quot;{query}&quot;{category !== 'All' ? ` in ${category}` : ''}
                  .
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setQuery('');
                    setCategory('All');
                  }}
                  className="text-sm font-bold text-accent hover:underline"
                >
                  Clear filters
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <FAQ />
    </WebLayout>
  );
}
